/**
 * The title bar every bench wears on a phone held upright: a way back at the
 * left, the bench's name in the middle, and light/dark and full screen at the
 * right. ONE component for the chemistry, optics and mechanics benches, so the
 * three bars cannot drift apart again — they had three backs (a word, a bare
 * arrow, a ringed arrow) and full screen in three different places.
 *
 * Shown only at ≤640px (see BenchPhoneHeader.css); each bench hides its own
 * bar at the same width. Sideways and on a desktop a bench keeps its own bar,
 * which has room for more than a phone's does.
 *
 * Colours come from the host through custom properties, as PaneButtons' do:
 *   --bench-accent     the bench's own colour: washed across the bar, and
 *                      down the top of the bench where the host puts the
 *                      `bench-accent-wash` class on its stage
 *   --bench-head-bg    the bar
 *   --bench-head-line  its bottom edge and the buttons' rings
 *   --bench-head-ink   the title
 *   --bench-head-muted the buttons' marks
 * and the host sets --pane-btn-* for the light/dark switch (ThemeToggle).
 */
import { FullscreenIcon } from './FullscreenIcon';
import { ThemeToggle } from './PaneButtons';
import './BenchPalette.css';
import './BenchPhoneHeader.css';

export interface BenchPhoneHeaderProps {
    title: React.ReactNode;
    /** Where back goes: a link where the host has a URL, else a handler. */
    back?: { label: string; href?: string; onClick?: () => void };
    /** Bench controls that belong beside back — the optics bench's undo/redo. */
    leading?: React.ReactNode;
    /** Anything the host adds before light/dark — the student app's "Guided". */
    trailing?: React.ReactNode;
    theme: 'light' | 'dark';
    onToggleTheme: () => void;
    fullscreen: { isFullscreen: boolean; toggle: () => void };
}

export function BenchPhoneHeader({
    title, back, leading, trailing, theme, onToggleTheme, fullscreen,
}: BenchPhoneHeaderProps): React.JSX.Element {
    const fsLabel = fullscreen.isFullscreen ? 'Leave full screen' : 'Full screen';
    return (
        <div className="bench-phone-header">
            <div className="bench-phone-header-start">
                {back && (back.href
                    ? <a className="bench-phone-header-btn" href={back.href} aria-label={back.label} title={back.label}>←</a>
                    : <button type="button" className="bench-phone-header-btn" onClick={back.onClick} aria-label={back.label} title={back.label}>←</button>)}
                {leading}
            </div>
            <h2 className="bench-phone-header-title">{title}</h2>
            <div className="bench-phone-header-end">
                {trailing}
                <ThemeToggle className="bench-phone-header-theme" theme={theme} onToggle={onToggleTheme} />
                <button type="button" className="bench-phone-header-btn" aria-pressed={fullscreen.isFullscreen}
                    onClick={fullscreen.toggle} aria-label={fsLabel} title={fsLabel}>
                    <FullscreenIcon exiting={fullscreen.isFullscreen} />
                </button>
            </div>
        </div>
    );
}
